import { BrandLogo, Button, Menu, MenuItem } from '@affine/component';
import { Loading } from '@affine/component/ui/loading';
import type { PDFViewerNavigation } from '@affine/core/blocksuite/attachment-viewer/pdf/pdf-navigation';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { GraphQLService } from '@affine/core/modules/cloud';
import type { DocReferenceInfo } from '@affine/core/modules/peek-view/entities/peek-view';
import { MailMessagePreview } from '@affine/core/modules/streamlain/mail/mail-message-preview';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  type MailMessageBodyQuery,
  mailMessageBodyQuery,
} from '@affine/graphql';
import { type I18nString, i18nTime, isI18nString, useI18n } from '@affine/i18n';
import type { SearchSourceType } from '@affine/reader';
import { ArrowDownSmallIcon, SearchIcon } from '@blocksuite/icons/rc';
import type { WorkspaceViewScope } from '@streamlain/copilot-realtime-contract';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  lazy,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';

import { DocsService } from '../../doc';
import { DocDisplayMetaService } from '../../doc-display-meta';
import {
  buildDocsSearchNavigation,
  createDocsSearchNavigationResolvers,
  type DocsSearchNavigablePayload,
  type DocsSearchNavigation,
} from '../../docs-search/services/source-navigation';
import { WorkbenchService } from '../../workbench';
import {
  type DocsBlockPayload,
  type DocsPayload,
  getDocsPayloadSourceTypes,
} from '../impls/docs-pdf-search';
import { SEARCH_DEGRADED_STATUS } from '../impls/docs-search-status';
import type { MailPayload } from '../impls/mail';
import { isPersonDocItem } from '../impls/people';
import { CMDKQuickSearchService, openedDocId } from '../services/cmdk';
import { PeopleSearchService } from '../services/people-search';
import { QuickSearchService } from '../services/quick-search';
import type { QuickSearchItem } from '../types/item';
import * as styles from './advanced-search-dialog.css';
import { ContentNavigation, SearchResultNavigation } from './cmdk';
import { DesktopSearchHint } from './desktop-search-hint';
import { groupQuickSearchItems } from './group-items';
import { HighlightText } from './highlight-text';
import { OnlineSearchFooter } from './online-search-footer';
import { PersonSearchFilter } from './person-filter';
import {
  searchResultRowAttributes,
  SearchResultRowContent,
  SelectionCursor,
} from './search-result-row';
import * as searchResults from './search-results.css';
import * as searchUi from './search-ui.css';
import { useIndexerSummary } from './use-indexer-summary';

const AttachmentPreviewPeekView = lazy(() =>
  import('@affine/core/modules/peek-view/view/attachment-preview').then(m => ({
    default: m.AttachmentPreviewPeekView,
  }))
);
const DocPeekPreview = lazy(() =>
  import('@affine/core/modules/peek-view/view/doc-preview').then(m => ({
    default: m.DocPeekPreview,
  }))
);

type SourceFilter = 'all' | SearchSourceType | 'mail';
type TimeFilter = 'all' | 'day' | 'week' | 'month';

// All source types stay visible as chips: scoping a search to PDFs, images or
// attachments is a primary intent, not something to hide behind a "more" menu.
// The dialog grows wider/taller instead.
const SOURCE_FILTERS: SourceFilter[] = [
  'all',
  'doc',
  'text',
  'database',
  'mail',
  'edgeless',
  'image',
  'attachment',
  'pdf',
  'embed',
];

const TIME_FILTERS: TimeFilter[] = ['all', 'day', 'week', 'month'];

const SPLIT_HANDLE_WIDTH = 1;
const MIN_SEARCH_PANE_WIDTH = 360;
const MIN_PREVIEW_PANE_WIDTH = 360;
const DEFAULT_SEARCH_PANE_RATIO = 0.56;
const KEYBOARD_RESIZE_STEP = 32;
const ADVANCED_SEARCH_LOADING_TIMEOUT_MS = 5_000;
const SEARCH_PANE_RATIO_STORAGE_KEY =
  'streamlain:advanced-search:search-pane-ratio';

const SOURCE_LABEL_KEY: Record<SourceFilter, string> = {
  all: 'com.streamlain.quicksearch.advanced.source.all',
  doc: 'com.streamlain.quicksearch.sourceType.doc',
  text: 'com.streamlain.quicksearch.sourceType.text',
  database: 'com.streamlain.quicksearch.sourceType.database',
  mail: 'com.streamlain.quicksearch.sourceType.mail',
  edgeless: 'com.streamlain.quicksearch.sourceType.edgeless',
  image: 'com.streamlain.quicksearch.sourceType.image',
  attachment: 'com.streamlain.quicksearch.sourceType.attachment',
  pdf: 'com.streamlain.quicksearch.sourceType.pdf',
  embed: 'com.streamlain.quicksearch.sourceType.embed',
};

const TIME_LABEL_KEY: Record<TimeFilter, string> = {
  all: 'com.streamlain.quicksearch.advanced.time.all',
  day: 'com.streamlain.quicksearch.advanced.time.day',
  week: 'com.streamlain.quicksearch.advanced.time.week',
  month: 'com.streamlain.quicksearch.advanced.time.month',
};

function readStoredSearchPaneRatio() {
  if (typeof window === 'undefined') return null;
  try {
    const value = Number(
      window.localStorage.getItem(SEARCH_PANE_RATIO_STORAGE_KEY)
    );
    return Number.isFinite(value) && value > 0 && value < 1 ? value : null;
  } catch {
    return null;
  }
}

function writeStoredSearchPaneRatio(ratio: number) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SEARCH_PANE_RATIO_STORAGE_KEY,
      ratio.toFixed(4)
    );
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
}

function statusKey(status: I18nString) {
  if (typeof status === 'string') return status;
  return `${status.i18nKey}:${JSON.stringify(status.options ?? {})}`;
}

function dedupeStatuses(statuses: (I18nString | null)[]) {
  const seen = new Set<string>();
  return statuses.filter((status): status is I18nString => {
    if (!status) return false;
    const key = statusKey(status);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Advanced search view; rendered inside the shared `SearchSurface` frame. */
export function AdvancedSearchView({
  navigationScope,
  collectionsEnabled = false,
  tagsEnabled = false,
  onClose,
  onReadyChange,
}: {
  navigationScope?: WorkspaceViewScope;
  collectionsEnabled?: boolean;
  tagsEnabled?: boolean;
  onClose: () => void;
  /** Ready once the search sessions exist and the first result set landed. */
  onReadyChange: (ready: boolean) => void;
}) {
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const cmdk = useService(CMDKQuickSearchService);
  const workspaceService = useService(WorkspaceService);
  const i18n = useI18n();
  const quickSearch = useService(QuickSearchService);
  const personFilter = useLiveData(quickSearch.personFilter$);
  const people = useLiveData(useService(PeopleSearchService).people$);
  // Sessions and query generation belong to the same owner in both views.
  // Mount/remount only changes the presentation, never creates/disposes sessions.
  const owner = quickSearch.quickSearch;
  const query = useLiveData(owner.query$);
  const ownerItems = useLiveData(owner.items$);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  // A source chip scopes the docs too: each lists and counts only its places
  // of that kind. Closing the dialog gives quick search every place again.
  useEffect(() => {
    quickSearch.placeSource$.next(
      sourceFilter === 'all' || sourceFilter === 'mail' ? null : sourceFilter
    );
  }, [quickSearch, sourceFilter]);
  useEffect(() => () => quickSearch.placeSource$.next(null), [quickSearch]);
  const [selectedId, setSelectedId] = useState('');
  const [searchTimedOut, setSearchTimedOut] = useState(false);
  const selectedTargetKeyRef = useRef('');
  const pendingActionIndexRef = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [searchPaneRatio, setSearchPaneRatio] = useState<number | null>(
    readStoredSearchPaneRatio
  );
  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0 || personFilter !== null;
  const [previewPaneWasOpened, setPreviewPaneWasOpened] = useState(hasQuery);
  const showPreviewPane = hasQuery || previewPaneWasOpened;
  const connectivityStatus = useConnectivityStatus();
  const connectivitySearchDegraded = useUiOfflineSignal(
    hasQuery &&
      workspaceService.workspace.flavour !== 'local' &&
      connectivityStatus.offline
  );

  const peopleItems = useMemo(
    () => ownerItems.filter(isPersonDocItem),
    [ownerItems]
  );
  const rawItems = useMemo(() => {
    const allowed =
      people.find(person => person.id === personFilter?.id)?.docIds ?? [];
    return ownerItems.filter(item => {
      if (isNavigationSearchItem(item)) return true;
      if (
        item.source !== 'docs' &&
        item.source !== 'people' &&
        !(hasSearchText(query) && item.source === 'mail')
      )
        return false;
      if (!personFilter) return true;
      const docId = (item.payload as { docId?: string })?.docId;
      return !!docId && allowed.includes(docId);
    });
  }, [ownerItems, query, personFilter, people]);
  const recentItems = useMemo(
    () => ownerItems.filter(item => item.source === 'recent-doc'),
    [ownerItems]
  );
  const docsLoading = useLiveData(owner.isLoading$);
  const docsSettled = useLiveData(owner.isSettled$);
  const loading =
    personFilter && !trimmedQuery
      ? false
      : docsLoading || (hasQuery && !docsSettled);
  const showSearchLoading =
    loading && !searchTimedOut && peopleItems.length === 0;
  const ownerOpen = useLiveData(owner.show$);
  const viewReady = ownerOpen && !showSearchLoading;
  useEffect(() => {
    onReadyChange(viewReady);
  }, [onReadyChange, viewReady]);
  useEffect(() => () => onReadyChange(false), [onReadyChange]);
  const error = useLiveData(owner.error$);
  const ownerStatus = useLiveData(owner.status$);
  const ownerFooter = useLiveData(owner.footer$);
  const ownerDesktopHint = useLiveData(owner.desktopHint$);
  const searchStatuses = useMemo(
    () =>
      dedupeStatuses([
        connectivitySearchDegraded ? SEARCH_DEGRADED_STATUS : null,
        ownerStatus,
      ]),
    [connectivitySearchDegraded, ownerStatus]
  );
  const degradedSearchStatus = searchStatuses[0] ?? null;
  const isSearchDegraded = degradedSearchStatus !== null;

  useEffect(() => {
    if (hasQuery) setPreviewPaneWasOpened(true);
  }, [hasQuery]);

  const items = useMemo(() => {
    // Advanced Search presents a stable result set. The compact Quick Search
    // may publish its fast local snapshot immediately, but showing that here
    // as "2 of 2" before the remote merge supplies result three makes the
    // result count and selection visibly jump.
    const settledItems =
      hasQuery && !docsSettled
        ? rawItems.filter(
            item => isPersonDocItem(item) || isNavigationSearchItem(item)
          )
        : rawItems;
    const minTimestamp = minTimestampForTimeFilter(timeFilter);
    return settledItems.filter(item => {
      if (isNavigationSearchItem(item)) return true;
      if (
        sourceFilter !== 'all' &&
        !getItemSourceTypes(item).includes(sourceFilter)
      ) {
        return false;
      }

      if (minTimestamp !== null) {
        const timestamp = getItemTimestamp(
          item,
          docsService,
          docDisplayMetaService
        );
        if (timestamp === null || timestamp < minTimestamp) return false;
      }

      return true;
    });
  }, [
    docDisplayMetaService,
    docsService,
    docsSettled,
    hasQuery,
    rawItems,
    sourceFilter,
    timeFilter,
  ]);

  useEffect(() => {
    if (!hasQuery || !loading || items.length > 0) {
      setSearchTimedOut(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setSearchTimedOut(true);
    }, ADVANCED_SEARCH_LOADING_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [hasQuery, items.length, loading, trimmedQuery]);

  const groups = useMemo(() => {
    const navigationItems = items.filter(isNavigationSearchItem);
    const content = groupQuickSearchItems(
      items.filter(item => !isNavigationSearchItem(item))
    );
    return navigationItems.length
      ? [
          {
            group: {
              id: 'advanced:app-navigation',
              label: {
                i18nKey: 'com.streamlain.quicksearch.advanced.appNavigation',
              },
            },
            items: navigationItems,
          },
          ...content,
        ]
      : content;
  }, [items]);

  const visibleGroups = useMemo(
    () =>
      hasQuery
        ? groups
        : [
            ...groups.filter(
              group => group.group?.id === 'advanced:app-navigation'
            ),
            ...groupQuickSearchItems(recentItems),
          ],
    [groups, hasQuery, recentItems]
  );
  // Keyboard selection and result bindings follow the actual rendered groups.
  const navItems = useMemo(
    () => visibleGroups.flatMap(group => group.items),
    [visibleGroups]
  );

  const selectItem = useCallback((item: QuickSearchItem) => {
    selectedTargetKeyRef.current = getItemNavigationIdentity(item);
    setSelectedId(item.id);
  }, []);

  useEffect(() => {
    const selectedItem = navItems.find(item => item.id === selectedId);
    if (selectedItem) {
      pendingActionIndexRef.current = null;
      selectedTargetKeyRef.current = getItemNavigationIdentity(selectedItem);
      return;
    }

    const previousTargetKey = selectedTargetKeyRef.current;
    if (previousTargetKey) {
      const restoredItem = navItems.find(
        item => getItemNavigationIdentity(item) === previousTargetKey
      );
      if (restoredItem) {
        setSelectedId(restoredItem.id);
        return;
      }
    }

    if (selectedId && loading) return;

    const pendingIndex = pendingActionIndexRef.current;
    if (pendingIndex !== null) {
      pendingActionIndexRef.current = null;
      const candidate =
        navItems[Math.min(pendingIndex, navItems.length - 1)] ??
        navItems[pendingIndex - 1];
      if (candidate) {
        selectedTargetKeyRef.current = getItemNavigationIdentity(candidate);
        setSelectedId(candidate.id);
        return;
      }
    }

    const fallbackItem = navItems[0];
    selectedTargetKeyRef.current = fallbackItem
      ? getItemNavigationIdentity(fallbackItem)
      : '';
    setSelectedId(fallbackItem?.id ?? '');
  }, [loading, navItems, selectedId, trimmedQuery]);

  const selectedItem = useMemo(
    () => navItems.find(item => item.id === selectedId) ?? null,
    [navItems, selectedId]
  );
  const selectedPreview = useMemo(
    () =>
      selectedItem
        ? getPreviewTarget(
            selectedItem,
            trimmedQuery,
            payload => resolveDocsSearchNavigation(payload, docsService),
            !!personFilter
          )
        : null,
    [docsService, selectedItem, trimmedQuery, personFilter]
  );
  const selectedMailItem = isMailSearchItem(selectedItem) ? selectedItem : null;
  const activeView = useLiveData(workbench.activeView$);

  const resultSummary = !hasQuery
    ? null
    : i18n.t({
        i18nKey: 'com.streamlain.quicksearch.advanced.results',
        options: {
          count: items.length,
          total: rawItems.length,
        },
      });
  const indexerSummary = useIndexerSummary();
  const showsPlaceholderState = navItems.length === 0;

  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      owner.setQuery(event.target.value);
    },
    [owner]
  );

  const focusSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
    });
  }, []);

  useEffect(() => {
    focusSearchInput();
  }, [focusSearchInput]);

  const handleOpen = useCallback(
    (item: QuickSearchItem | null, inSplit = false) => {
      if (!item) return;
      if (inSplit) {
        if (item.beforeSubmit && !item.beforeSubmit()) return;
        cmdk.activateResult(item, { collectionsEnabled, tagsEnabled }, true);
        onClose();
      } else {
        owner.submit(item);
      }
    },
    [cmdk, collectionsEnabled, tagsEnabled, owner, onClose]
  );

  const handleSubmit = useCallback(
    (item: QuickSearchItem | null, inSplit = false) => {
      if (!item) return;
      if (item.beforeSubmit && !item.beforeSubmit()) {
        const actionIndex = navItems.findIndex(
          navItem => navItem.id === item.id
        );
        pendingActionIndexRef.current = actionIndex >= 0 ? actionIndex : null;
        selectItem(item);
        return;
      }
      handleOpen(item, inSplit);
    },
    [handleOpen, navItems, selectItem]
  );

  const moveSelection = useCallback(
    (delta: number) => {
      if (navItems.length === 0) return;
      const index = navItems.findIndex(item => item.id === selectedId);
      const nextIndex =
        index < 0
          ? 0
          : Math.min(navItems.length - 1, Math.max(0, index + delta));
      selectItem(navItems[nextIndex]);
    },
    [navItems, selectItem, selectedId]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Filters, menus and preview controls own their keyboard activation.
      const target = event.target;
      if (
        event.defaultPrevented ||
        !(target instanceof HTMLElement) ||
        (target !== searchInputRef.current &&
          !target.closest('button')?.classList.contains(styles.resultItem))
      )
        return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(selectedItem, event.metaKey || event.ctrlKey);
      }
    },
    [handleSubmit, moveSelection, selectedItem]
  );

  const setClampedSearchPaneWidth = useCallback((nextWidth: number) => {
    const contentWidth = contentRef.current?.getBoundingClientRect().width ?? 0;
    const availableWidth = contentWidth - SPLIT_HANDLE_WIDTH;
    if (availableWidth <= 0) return;
    const maxWidth = Math.max(
      MIN_SEARCH_PANE_WIDTH,
      availableWidth - MIN_PREVIEW_PANE_WIDTH
    );
    const clampedWidth = Math.min(
      maxWidth,
      Math.max(MIN_SEARCH_PANE_WIDTH, nextWidth)
    );
    const nextRatio = clampedWidth / availableWidth;
    setSearchPaneRatio(nextRatio);
    writeStoredSearchPaneRatio(nextRatio);
  }, []);

  const startSplitResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!showPreviewPane || !contentRef.current) return;
      if (window.matchMedia('(max-width: 900px)').matches) return;
      event.preventDefault();
      event.stopPropagation();

      const rect = contentRef.current.getBoundingClientRect();
      const updateFromClientX = (clientX: number) => {
        setClampedSearchPaneWidth(clientX - rect.left);
      };
      updateFromClientX(event.clientX);

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handlePointerMove = (moveEvent: PointerEvent) => {
        moveEvent.preventDefault();
        updateFromClientX(moveEvent.clientX);
      };
      const stopResize = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', stopResize);
        window.removeEventListener('pointercancel', stopResize);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', stopResize);
      window.addEventListener('pointercancel', stopResize);
    },
    [setClampedSearchPaneWidth, showPreviewPane]
  );

  const handleSplitResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      const contentWidth =
        contentRef.current?.getBoundingClientRect().width ?? 0;
      const availableWidth = Math.max(0, contentWidth - SPLIT_HANDLE_WIDTH);
      const currentWidth =
        availableWidth * (searchPaneRatio ?? DEFAULT_SEARCH_PANE_RATIO);
      setClampedSearchPaneWidth(
        currentWidth +
          (event.key === 'ArrowLeft'
            ? -KEYBOARD_RESIZE_STEP
            : KEYBOARD_RESIZE_STEP)
      );
    },
    [searchPaneRatio, setClampedSearchPaneWidth]
  );

  const contentStyle = useMemo(
    () =>
      showPreviewPane && searchPaneRatio !== null
        ? ({
            '--advanced-search-pane-width': `clamp(${MIN_SEARCH_PANE_WIDTH}px, calc(${(
              searchPaneRatio * 100
            ).toFixed(4)}% - ${(SPLIT_HANDLE_WIDTH * searchPaneRatio).toFixed(
              4
            )}px), calc(100% - ${
              SPLIT_HANDLE_WIDTH + MIN_PREVIEW_PANE_WIDTH
            }px))`,
          } as CSSProperties)
        : undefined,
    [searchPaneRatio, showPreviewPane]
  );

  return (
    <ContentNavigation
      enabled={!!navigationScope}
      scope={navigationScope}
      groups={visibleGroups}
      settled={docsSettled}
    >
      <div
        ref={contentRef}
        className={clsx(
          styles.content,
          !showPreviewPane && styles.contentSingle
        )}
        data-has-preview={showPreviewPane}
        style={contentStyle}
        onKeyDown={handleKeyDown}
      >
        <section className={styles.searchPane}>
          <div className={styles.controls}>
            <label className={searchUi.field}>
              <SearchIcon />
              <input
                ref={searchInputRef}
                className={searchUi.fieldInput}
                autoFocus
                value={query}
                onChange={handleQueryChange}
                placeholder={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.placeholder',
                })}
              />
              {showSearchLoading ? <Loading size={18} /> : null}
            </label>
            <div className={styles.filterRow}>
              {SOURCE_FILTERS.map(filter => (
                <button
                  key={filter}
                  type="button"
                  className={searchUi.chip}
                  data-active={sourceFilter === filter ? 'true' : 'false'}
                  onClick={() => setSourceFilter(filter)}
                >
                  {i18n.t({ i18nKey: SOURCE_LABEL_KEY[filter] })}
                </button>
              ))}
              <span className={styles.filterDivider} />
              <Menu
                items={TIME_FILTERS.map(filter => (
                  <MenuItem
                    key={filter}
                    selected={timeFilter === filter}
                    onSelect={() => setTimeFilter(filter)}
                  >
                    {i18n.t({ i18nKey: TIME_LABEL_KEY[filter] })}
                  </MenuItem>
                ))}
              >
                <button
                  type="button"
                  className={clsx(searchUi.chip, styles.chipDropdown)}
                  data-active={timeFilter !== 'all' ? 'true' : 'false'}
                >
                  {i18n.t({ i18nKey: TIME_LABEL_KEY[timeFilter] })}
                  <ArrowDownSmallIcon />
                </button>
              </Menu>
              <PersonSearchFilter />
            </div>
            <div className={styles.statusLine}>
              {resultSummary ? <span>{resultSummary}</span> : null}
              {error ? (
                <span className={styles.errorText}>
                  {UserFriendlyError.fromAny(error).message}
                </span>
              ) : null}
            </div>
          </div>
          <div
            className={clsx(
              styles.resultList,
              showsPlaceholderState && styles.resultListEmpty
            )}
          >
            <SelectionCursor rowSelector={`.${styles.resultItem}`} />
            {!hasQuery && navItems.length === 0 ? (
              <EmptyState
                title={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.emptyQuery',
                })}
                description={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.emptyQueryDesc',
                })}
              />
            ) : hasQuery && items.length === 0 && showSearchLoading ? (
              <EmptyState
                icon={<Loading size={28} />}
                title={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.searching',
                })}
                description={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.searchingDesc',
                })}
              />
            ) : hasQuery && items.length === 0 && !showSearchLoading ? (
              <EmptyState
                title={i18n.t({
                  i18nKey:
                    indexerSummary && rawItems.length === 0
                      ? 'com.streamlain.quicksearch.advanced.noResultsIndexing'
                      : 'com.streamlain.quicksearch.advanced.noResults',
                })}
                description={i18n.t({
                  i18nKey:
                    rawItems.length === 0
                      ? 'com.streamlain.quicksearch.advanced.noResultsDesc'
                      : 'com.streamlain.quicksearch.advanced.filteredNoResultsDesc',
                })}
              />
            ) : (
              visibleGroups.map(({ group, items }) => (
                <AdvancedResultGroup
                  groupLabel={group ? i18n.t(group.label) : undefined}
                  key={group?.id ?? 'ungrouped'}
                  items={items}
                  selectedId={selectedId}
                  onSubmit={handleSubmit}
                  onSelect={selectItem}
                />
              ))
            )}
          </div>
          <div className={styles.searchFooter}>
            {!ownerFooter && isSearchDegraded ? (
              <span className={styles.statusPill}>
                {i18n.t(degradedSearchStatus)}
              </span>
            ) : null}
            {ownerFooter ? (
              <OnlineSearchFooter
                label={i18n.t(ownerFooter.label)}
                onClick={ownerFooter.action}
                busy={ownerFooter.busy}
              />
            ) : null}
            <div className={styles.kbdHint}>
              <span>
                <kbd className={styles.kbd}>↑</kbd>
                <kbd className={styles.kbd}>↓</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.navigate',
                })}
              </span>
              <span>
                <kbd className={styles.kbd}>↵</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.open',
                })}
              </span>
              <span>
                <kbd className={styles.kbd}>esc</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.close',
                })}
              </span>
            </div>
          </div>
          <DesktopSearchHint visible={ownerDesktopHint} />
        </section>
        {showPreviewPane ? (
          <div
            className={styles.splitResizeHandle}
            role="separator"
            aria-orientation="vertical"
            aria-label={i18n.t({
              i18nKey: 'com.streamlain.quicksearch.advanced.resizePreview',
            })}
            tabIndex={0}
            onKeyDown={handleSplitResizeKeyDown}
            onPointerDown={startSplitResize}
          />
        ) : null}
        {showPreviewPane ? (
          <aside className={styles.previewPane}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>
                  {selectedPreview ? (
                    // The preview shows a doc, so its header names that doc,
                    // also for block hits and "show more" rows.
                    <PreviewDocTitle docId={selectedPreview.docId} />
                  ) : selectedItem ? (
                    getPlainItemTitle(selectedItem, i18n.t)
                  ) : (
                    i18n.t({
                      i18nKey:
                        'com.streamlain.quicksearch.advanced.previewFallbackTitle',
                    })
                  )}
                </div>
              </div>
            </div>
            <div className={styles.previewBody}>
              {selectedMailItem ? (
                <div className={styles.previewCard}>
                  <AdvancedMailPreview item={selectedMailItem} />
                </div>
              ) : selectedPreview ? (
                <div className={styles.previewCard}>
                  <FrameworkScope scope={activeView.scope}>
                    <Suspense fallback={null}>
                      {selectedPreview.kind === 'attachment' ? (
                        // A PDF hit shows its page in the PDF viewer, not the
                        // doc around the attachment.
                        <AttachmentPreviewPeekView
                          key={selectedPreview.blockId}
                          docId={selectedPreview.docId}
                          blockId={selectedPreview.blockId}
                          navigation={selectedPreview.navigation}
                          loader={previewLoader}
                          hideThumbnails
                        />
                      ) : (
                        <DocPeekPreview
                          docRef={selectedPreview.docRef}
                          hideReadOnlyPermissionBanner
                          loader={previewLoader}
                        />
                      )}
                    </Suspense>
                  </FrameworkScope>
                </div>
              ) : (
                <div className={styles.previewEmpty}>
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.previewEmpty',
                  })}
                </div>
              )}
            </div>
            {selectedItem ? (
              <div className={styles.previewFooter}>
                <Button
                  variant="secondary"
                  onClick={() => handleOpen(selectedItem, true)}
                >
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.openInSplit',
                  })}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleOpen(selectedItem)}
                >
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.openResult',
                  })}
                </Button>
              </div>
            ) : null}
          </aside>
        ) : null}
      </div>
    </ContentNavigation>
  );
}

const previewLoader = (
  <div className={styles.previewLoader}>
    <BrandLogo size={40} animate />
  </div>
);

function PreviewDocTitle({ docId }: { docId: string }) {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const title$ = useMemo(
    () => docDisplayMetaService.title$(docId),
    [docDisplayMetaService, docId]
  );
  return <>{useLiveData(title$)}</>;
}

function AdvancedMailPreview({
  item,
}: {
  item: QuickSearchItem<'mail', MailPayload> & { payload: MailPayload };
}) {
  const i18n = useI18n();
  const graphql = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const payload = item.payload;
  const messageId = payload?.messageId?.trim() || null;
  const body = useSWR<MailMessageBodyQuery['mailMessageBody']>(
    messageId ? ['advancedSearchMailPreview', workspaceId, messageId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailMessageBodyQuery,
        variables: {
          workspaceId,
          messageId: messageId as string,
        },
      });
      return response.mailMessageBody;
    },
    { suspense: false }
  );
  const [, subtitle] = getItemTitleParts(item, i18n.t);

  return (
    <MailMessagePreview
      className={styles.advancedMailPreview}
      fitHtmlToWidth
      message={
        messageId
          ? {
              id: messageId,
              subject: payload?.subject,
              from: payload?.from,
              receivedAt: payload?.receivedAt,
              snippet: stripSearchHighlightTags(subtitle ?? ''),
              sizeBytes: body.data?.sizeBytes,
            }
          : null
      }
      body={body.data}
      isLoading={body.isLoading}
      error={body.error instanceof Error ? body.error.message : undefined}
    />
  );
}

function isMailSearchItem(
  item: QuickSearchItem | null
): item is QuickSearchItem<'mail', MailPayload> & { payload: MailPayload } {
  return item?.source === 'mail' && Boolean(item.payload);
}

function EmptyState({
  description,
  icon,
  title,
}: {
  title: ReactNode;
  description: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={styles.emptyState}>
      {icon ?? <SearchIcon />}
      <div className={styles.emptyTitle}>{title}</div>
      <div className={styles.emptyDescription}>{description}</div>
    </div>
  );
}

function AdvancedResultGroup({
  groupLabel,
  items,
  onSubmit,
  onSelect,
  selectedId,
}: {
  groupLabel?: string;
  items: QuickSearchItem[];
  selectedId: string;
  onSelect: (item: QuickSearchItem) => void;
  onSubmit: (item: QuickSearchItem | null) => void;
}) {
  return (
    <>
      {groupLabel ? (
        <div className={searchUi.groupHeading}>{groupLabel}</div>
      ) : null}
      {items.map((item, index) => (
        <AdvancedResultItem
          key={item.id}
          items={items}
          index={index}
          selected={item.id === selectedId}
          onSubmit={onSubmit}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function AdvancedResultItem({
  items,
  index,
  onSelect,
  onSubmit,
  selected,
}: {
  items: QuickSearchItem[];
  index: number;
  selected: boolean;
  onSelect: (item: QuickSearchItem) => void;
  onSubmit: (item: QuickSearchItem | null) => void;
}) {
  const i18n = useI18n();
  const ref = useRef<HTMLButtonElement>(null);
  const item = items[index];
  const [title, subtitle] = getItemTitleParts(item, i18n.t);
  const Icon = typeof item.icon === 'function' ? item.icon : null;
  const iconNode = Icon ? (
    <Icon />
  ) : isRenderableIcon(item.icon) ? (
    item.icon
  ) : null;

  useEffect(() => {
    if (selected) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  return (
    <SearchResultNavigation
      item={item}
      onSubmit={onSubmit}
      userActivation="double-click"
    >
      <button
        ref={ref}
        type="button"
        data-selected={selected ? 'true' : 'false'}
        {...(item.row
          ? {
              ...searchResultRowAttributes(items, index),
              className: clsx(styles.resultItem, searchResults.row),
            }
          : { className: styles.resultItem })}
        onClick={event => {
          onSelect(item);
          // "Show N more" lists the doc's other places on the first click; a
          // double click must not fold them away again.
          if (item.row === 'more' && event.detail <= 1) onSubmit(item);
        }}
        onDoubleClick={item.row === 'more' ? undefined : () => onSubmit(item)}
      >
        {item.row ? (
          <SearchResultRowContent item={item} />
        ) : (
          <>
            <span className={styles.resultIcon}>{iconNode}</span>
            <span className={styles.resultText}>
              <span className={styles.resultTitle}>
                <span className={styles.resultTitleText}>
                  <HighlightText text={title} start="<b>" end="</b>" />
                </span>
                {item.badge ? (
                  <span className={styles.resultBadge}>
                    {i18n.t(item.badge)}
                  </span>
                ) : null}
              </span>
              {subtitle ? (
                <span className={styles.resultSubtitle}>
                  <HighlightText text={subtitle} start="<b>" end="</b>" />
                </span>
              ) : null}
            </span>
            <span className={styles.resultMeta}>
              {item.timestamp ? i18nTime(new Date(item.timestamp)) : null}
            </span>
          </>
        )}
      </button>
    </SearchResultNavigation>
  );
}

function isRenderableIcon(
  icon: QuickSearchItem['icon']
): icon is Exclude<ReactNode, undefined | null | boolean> {
  return (
    icon !== undefined &&
    icon !== null &&
    icon !== false &&
    icon !== true &&
    typeof icon !== 'function'
  );
}

function getItemTitle(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
) {
  return getItemTitleParts(item, t)[0];
}

function getPlainItemTitle(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
) {
  return stripSearchHighlightTags(getItemTitle(item, t));
}

function stripSearchHighlightTags(text: string) {
  return text.replaceAll('<b>', '').replaceAll('</b>', '');
}

function getItemTitleParts(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
): [string, string | null] {
  if (isI18nString(item.label)) {
    return [t(item.label), null];
  }
  return [
    t(item.label.title),
    item.label.subTitle ? t(item.label.subTitle) : null,
  ];
}

type PreviewTarget =
  | { kind: 'doc'; docId: string; docRef: DocReferenceInfo }
  | {
      kind: 'attachment';
      docId: string;
      blockId: string;
      navigation: PDFViewerNavigation;
    };

function docPreview(docRef: DocReferenceInfo): PreviewTarget {
  return { kind: 'doc', docId: docRef.docId, docRef };
}

function getPreviewTarget(
  item: QuickSearchItem,
  query: string | undefined,
  resolveNavigation: (
    payload: DocsSearchNavigablePayload
  ) => DocsSearchNavigation | null,
  wholeDoc = false
): PreviewTarget | null {
  if (item.source === 'mail') return null;
  const sidebarDocId = openedDocId(item);
  if (sidebarDocId) {
    return docPreview({
      docId: sidebarDocId,
      mode: 'edgeless',
      fitToContent: true,
    });
  }
  if (item.source === 'commands') return null;
  if (isPersonDocItem(item) || wholeDoc) {
    const docId = (item.payload as { docId?: string })?.docId;
    return docId
      ? docPreview({ docId, mode: 'edgeless', fitToContent: true })
      : null;
  }

  const payload = item.payload as DocsPayload | undefined;
  if (!payload) return null;

  const navigation = resolveNavigation(payload as DocsSearchNavigablePayload);
  if (!navigation) return null;

  if (navigation.kind === 'attachment') {
    return {
      kind: 'attachment',
      docId: navigation.docId,
      blockId: navigation.attachmentBlockId,
      navigation: {
        pageIndex: navigation.pageIndex,
        query: navigation.query,
        highlightRects: navigation.highlightRects,
        requestId: item.id,
      },
    };
  }

  const blockPayload = isDocsBlockPayload(payload) ? payload : null;
  const databaseId =
    blockPayload?.sourceType === 'database'
      ? (blockPayload.databaseId ?? blockPayload.blockId)
      : undefined;

  return docPreview({
    docId: navigation.docId,
    blockIds: navigation.blockIds,
    elementIds: navigation.elementIds,
    mode: navigation.mode,
    parentBlockId: blockPayload?.parentBlockId,
    parentFlavour: blockPayload?.parentFlavour,
    noteBlockId: blockPayload?.noteBlockId,
    databaseDocId: databaseId ? navigation.docId : undefined,
    databaseId,
    databaseRowId: blockPayload?.databaseRowId,
    query,
    requestId: item.id,
    fitToContent:
      !navigation.blockIds?.length && !navigation.elementIds?.length,
  });
}

function isDocsBlockPayload(payload: DocsPayload): payload is DocsBlockPayload {
  return !('kind' in payload);
}

function resolveDocsSearchNavigation(
  payload: DocsSearchNavigablePayload,
  docsService: DocsService
): DocsSearchNavigation | null {
  return buildDocsSearchNavigation(
    payload,
    createDocsSearchNavigationResolvers(docsService)
  );
}

function getItemNavigationIdentity(item: QuickSearchItem) {
  if (item.source === 'mail') {
    const payload = item.payload as MailPayload | undefined;
    return ['mail', payload?.messageId ?? '', payload?.route ?? item.id].join(
      '\u0000'
    );
  }

  const payload = item.payload as DocsPayload | undefined;
  if (!payload) return `item:${item.id}`;

  if ('kind' in payload) {
    return [
      'pdf',
      payload.kind,
      payload.docId,
      payload.attachmentBlockId,
      payload.pageIndex,
    ].join('\u0000');
  }

  const isActionRow =
    item.beforeSubmit && !payload.blockId && !payload.elementId;
  if (isActionRow) return `action:${item.id}`;

  return [
    'doc',
    payload.sourceType ?? 'doc',
    payload.docId ?? '',
    payload.blockId ?? '',
    payload.elementId ?? '',
    payload.databaseId ?? '',
    payload.databaseRowId ?? '',
    payload.databaseColumnId ?? '',
  ].join('\u0000');
}

function getItemSourceTypes(item: QuickSearchItem) {
  if (item.source === 'mail') return ['mail'];
  if (isPersonDocItem(item)) return ['people'];

  const payload = item.payload as DocsPayload | undefined;
  return getDocsPayloadSourceTypes(payload);
}

function getItemTimestamp(
  item: QuickSearchItem,
  docsService: DocsService,
  docDisplayMetaService: DocDisplayMetaService
): number | null {
  if (typeof item.timestamp === 'number') return item.timestamp;
  const docId = (item.payload as { docId?: string } | undefined)?.docId;
  if (!docId) return null;
  const doc = docsService.list.doc$(docId).value;
  if (!doc) return null;
  const updatedDate = docDisplayMetaService.getDocDisplayMeta(doc).updatedDate;
  return updatedDate ? new Date(updatedDate).getTime() : null;
}

function minTimestampForTimeFilter(filter: TimeFilter) {
  if (filter === 'all') return null;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (filter === 'day') return now - day;
  if (filter === 'week') return now - 7 * day;
  return now - 30 * day;
}

function hasSearchText(query: string) {
  return query.trim().length > 0;
}

function isNavigationSearchItem(item: QuickSearchItem): boolean {
  return (
    item.source === 'commands' &&
    !!item.payload?.navigation &&
    item.payload.navigation.definition.capability !== 'workspace-search' &&
    item.payload.navigation.definition.available()
  );
}
